import React, { useContext, useCallback, useState } from 'react'
import { Pressable, View, StyleSheet } from 'react-native'
import { ThemeContext } from '@pff-consumer/utils/lib/theme/context/theme-context'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { getColorVariants } from './util/get-color-variants'
import { getPlatformSpecificText } from '../text/getPlatformSpecificText'
import { ButtonProps } from './button.props'
import { useTheme } from '../../styles/theme'
import { useStyles } from './button.style'
import { Icon } from '../icon/icon'
import { CircleLoader } from '../circle-loader/circle-loader.web'

const SMALL_ICON_SIZE = 16
const MD_LG_ICON_SIZE = 20

export const Button = (props: ButtonProps) => {
  const {
    disabled,
    onPress,
    onFocus,
    onBlur,
    title,
    variant = 'primary',
    isLoading,
    loadingText,
    accessibilityLabel,
    testID = '',
    iconName,
    iconGroup,
    noWrap,
    size,
    iconSize,
    iconHoverEffect = false,
    style = {},
    isCircleLoaderRequired = false,
  } = props
  const [isHovered, setIsHovered] = useState(false)
  const { isWeb } = useContext(ThemeContext)
  const { Text, TextDescriptionVariant } = getPlatformSpecificText(isWeb)
  const styles = useStyles(props)
  const theme = useTheme()
  const onPressHandler = useCallback(() => {
    if (!disabled && !isLoading) {
      onPress()
    }
  }, [disabled, isLoading, onPress])

  const textWrap = noWrap ? 'noWrap' : 'wrap'
  const iconSizes = iconSize || (size === 'sm' ? SMALL_ICON_SIZE : MD_LG_ICON_SIZE)
  const colorVariant = getColorVariants(theme, variant, isLoading)
  const buttonBackgroundColor = useSharedValue(colorVariant.bg)
  const buttonBorderColor = useSharedValue(colorVariant.border)
  const animatedStyles = useAnimatedStyle(
    () => ({
      backgroundColor: buttonBackgroundColor.value,
      borderColor: buttonBorderColor.value,
    }),
    []
  )
  const textColor = isHovered ? colorVariant.textHover : colorVariant.text

  const onHoverIn = useCallback(() => {
    if (!disabled && !isLoading) {
      buttonBackgroundColor.value = withTiming(colorVariant.bgHover, { duration: 250 })
      buttonBorderColor.value = withTiming(colorVariant.borderHover, { duration: 250 })
      setIsHovered(true)
    }
  }, [])

  const onHoverOut = useCallback(() => {
    buttonBackgroundColor.value = withTiming(colorVariant.bg)
    buttonBorderColor.value = withTiming(colorVariant.border)
    setIsHovered(false)
  }, [])

  const onFocusHandler = useCallback(() => {
    if (!disabled && !isLoading) {
      onFocus?.()
    }
  }, [])

  const onBlurHandler = useCallback(() => {
    if (!disabled && !isLoading) {
      onBlur?.()
    }
  }, [])

  return (
    <Animated.View style={[styles.container, animatedStyles, style]}>
      <Pressable
        style={styles.pressable}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        onPress={onPressHandler}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        accessibilityLabel={accessibilityLabel}
        testID={testID}
        role='button'
      >
        {isLoading ? (
          <View style={styles.contentContainer}>
            {isCircleLoaderRequired && <CircleLoader variant={variant === 'Apple' ? variant : 'Loading'} />}
            <Text
              textAlign='center'
              variant={TextDescriptionVariant.TEXT_BASE_SEMIBOLD}
              colorOverride={textColor}
              textWrap={textWrap}
            >
              {loadingText}
            </Text>
          </View>
        ) : (
          <View style={styles.contentContainer}>
            <Text
              textAlign='center'
              variant={TextDescriptionVariant.TEXT_BASE_SEMIBOLD}
              colorOverride={textColor}
              textWrap={textWrap}
            >
              {title}
            </Text>
            {iconName && iconGroup && (
              <View testID='button-icon'>
                <Icon
                  style={StyleSheet.flatten([styles.icon, iconHoverEffect && isHovered ? styles.iconHover : null])}
                  group={iconGroup}
                  name={iconName}
                  fill={textColor}
                  width={iconSizes}
                  height={iconSizes}
                />
              </View>
            )}
          </View>
        )}
      </Pressable>
    </Animated.View>
  )
}
Button.displayName = 'Button'
